import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  User,
  CalendarDays,
  MapPin,
  Palette,
  UploadCloud,
  Check,
  ArrowRight,
  ArrowLeft,
  Copy,
  Loader2,
  ShieldCheck,
  MessageCircle,
  FileText,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useContent } from "@/context/ContentContext";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EVENT_TYPES,
  GUEST_OPTIONS,
  BANKS,
  QRIS_URL,
  WHATSAPP_NUMBER,
  formatRupiah,
} from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { n: "01", title: "Data Client", icon: User },
  { n: "02", title: "Detail Acara", icon: CalendarDays },
  { n: "03", title: "Lokasi Acara", icon: MapPin },
  { n: "04", title: "Konsep Dekorasi", icon: Palette },
];

const emptyForm = {
  namaLengkap: "",
  whatsapp: "",
  email: "",
  alamat: "",
  jenisAcara: "",
  tanggalAcara: "",
  waktuAcara: "",
  jumlahTamu: "",
  namaTempat: "",
  alamatLengkap: "",
  linkMaps: "",
  fotoLokasi: null,
  fotoLokasiUrl: "",
  paket: "",
  tema: "",
  warnaDominan: "",
  fotoReferensi: null,
  fotoReferensiUrl: "",
  catatanTambahan: "",
};

const FileUpload = ({ label, description, value, previewUrl, onUpload, testid, uploading }) => {
  const ref = useRef();
  return (
    <div>
      <Label className="text-xs font-semibold tracking-widest uppercase text-[#967C52]">
        {label}
      </Label>
      <div
        onClick={() => ref.current?.click()}
        className="mt-2 cursor-pointer rounded-xl border-2 border-dashed border-[#E5D7C5] bg-[#FFFDF9] hover:border-[#C5A059] transition-colors p-6 flex flex-col items-center justify-center text-center"
        data-testid={testid}
      >
        {previewUrl ? (
          <div className="w-full flex flex-col items-center gap-3">
            <img src={previewUrl} alt="preview" className="h-32 w-auto rounded-lg object-cover" />
            <span className="text-xs text-[#2E7D32] font-semibold flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Berhasil diunggah — klik untuk ganti
            </span>
          </div>
        ) : uploading ? (
          <Loader2 className="w-7 h-7 text-[#C5A059] animate-spin" />
        ) : (
          <>
            <UploadCloud className="w-8 h-8 text-[#C5A059] mb-2" />
            <span className="text-sm text-[#4A443F] font-medium">
              Klik untuk mengunggah foto
            </span>
            {description && (
              <span className="text-xs text-[#786F66] mt-1">{description}</span>
            )}
          </>
        )}
        <input
          ref={ref}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
        />
      </div>
    </div>
  );
};

const FieldLabel = ({ children }) => (
  <Label className="text-xs font-semibold tracking-widest uppercase text-[#967C52]">
    {children}
  </Label>
);

export const BookingSection = ({ selectedPackage }) => {
  const [mode, setMode] = useState("form"); // form | summary | payment | success
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...emptyForm, paket: selectedPackage || "" });
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [agree, setAgree] = useState(false);
  const [booking, setBooking] = useState(null);
  const [payMethod, setPayMethod] = useState("Bank Transfer");
  const [proof, setProof] = useState({ path: "", url: "" });
  const [pkgList, setPkgList] = useState([]);
  const { content } = useContent();
  const banks = content.payment?.banks?.length ? content.payment.banks : BANKS;
  const qris = content.payment?.qrisUrl || QRIS_URL;
  const waNum = content.contact?.whatsappNumber || WHATSAPP_NUMBER;

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPkgList(r.data)).catch(() => {});
  }, []);

  // sync selected package from packages section
  useEffect(() => {
    if (selectedPackage) setForm((f) => ({ ...f, paket: selectedPackage }));
  }, [selectedPackage]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFile = async (key, file) => {
    setUploading((u) => ({ ...u, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd);
      set(key, data.path);
      set(`${key}Url`, `${BACKEND_URL}${data.url}`);
      toast.success("Foto berhasil diunggah");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunggah foto");
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.namaLengkap || !form.whatsapp || !form.email || !form.alamat) {
        toast.error("Lengkapi semua data client");
        return false;
      }
    }
    if (step === 1) {
      if (!form.jenisAcara || !form.tanggalAcara || !form.waktuAcara || !form.jumlahTamu) {
        toast.error("Lengkapi semua detail acara");
        return false;
      }
    }
    if (step === 2) {
      if (!form.namaTempat || !form.alamatLengkap) {
        toast.error("Lengkapi nama tempat dan alamat venue");
        return false;
      }
    }
    if (step === 3) {
      if (!form.paket) {
        toast.error("Pilih paket dekorasi");
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (step < 3) setStep(step + 1);
    else setMode("summary");
  };
  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  const selectedPkgObj = pkgList.find((p) => p.name === form.paket);
  const total = selectedPkgObj?.price || 0;
  const dp = Math.floor(total / 2);

  const submitBooking = async () => {
    if (!agree) {
      toast.error("Harap menyetujui data booking terlebih dahulu");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client: {
          namaLengkap: form.namaLengkap,
          whatsapp: form.whatsapp,
          email: form.email,
          alamat: form.alamat,
        },
        event: {
          jenisAcara: form.jenisAcara,
          tanggalAcara: form.tanggalAcara,
          waktuAcara: form.waktuAcara,
          jumlahTamu: form.jumlahTamu,
        },
        location: {
          namaTempat: form.namaTempat,
          alamatLengkap: form.alamatLengkap,
          linkMaps: form.linkMaps,
          fotoLokasi: form.fotoLokasi,
        },
        concept: {
          paket: form.paket,
          tema: form.tema,
          warnaDominan: form.warnaDominan,
          fotoReferensi: form.fotoReferensi,
          catatanTambahan: form.catatanTambahan,
        },
      };
      const { data } = await axios.post(`${API}/bookings`, payload);
      setBooking(data);
      setMode("payment");
      window.scrollTo({ top: document.querySelector("#booking").offsetTop - 60, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim booking");
    } finally {
      setSubmitting(false);
    }
  };

  const submitPayment = async () => {
    if (!proof.path) {
      toast.error("Upload bukti pembayaran terlebih dahulu");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.booking_code}/payment`, {
        payment_method: payMethod,
        payment_proof: proof.path,
      });
      setBooking(data);
      setMode("success");
      window.scrollTo({ top: document.querySelector("#booking").offsetTop - 60, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim bukti pembayaran");
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = (t) => {
    navigator.clipboard.writeText(t.replace(/-/g, ""));
    toast.success("Nomor rekening disalin");
  };

  const resetAll = () => {
    setForm({ ...emptyForm });
    setStep(0);
    setAgree(false);
    setBooking(null);
    setProof({ path: "", url: "" });
    setMode("form");
  };

  return (
    <section id="booking" className="py-24 bg-[#FFFDF9]" data-testid="booking-section">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">
            Booking
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1C1917] tracking-tight">
            Formulir Pemesanan Dekorasi
          </h2>
          <p className="mt-4 text-[#786F66]">
            Isi data berikut untuk mewujudkan dekorasi impian Anda. Pilih tanggal
            sesuai rencana acara Anda.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {/* ---------------- FORM ---------------- */}
          {mode === "form" && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-3xl border border-[#E5D7C5] shadow-[0_4px_20px_rgba(28,25,23,0.05)] p-6 sm:p-10"
            >
              {/* stepper */}
              <div className="flex items-center justify-between mb-10">
                {STEPS.map((s, i) => (
                  <div key={s.n} className="flex-1 flex items-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all ${
                          i <= step
                            ? "bg-[#C5A059] border-[#C5A059] text-white"
                            : "bg-white border-[#E5D7C5] text-[#C5A059]"
                        }`}
                        data-testid={`step-indicator-${i + 1}`}
                      >
                        {i < step ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                      </div>
                      <span className="mt-2 text-[9px] sm:text-[10px] font-semibold tracking-widest uppercase text-[#786F66] hidden sm:block text-center max-w-[80px]">
                        {s.title}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-[#C5A059]" : "bg-[#E5D7C5]"}`} />
                    )}
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <span className="font-mono text-sm text-[#C5A059]">{STEPS[step].n}</span>
                <h3 className="font-serif text-2xl text-[#1C1917] mt-1">{STEPS[step].title}</h3>
              </div>

              {step === 0 && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <FieldLabel>Nama Lengkap</FieldLabel>
                    <Input className="mt-2" data-testid="input-client-name" placeholder="Contoh: Budi & Annisa" value={form.namaLengkap} onChange={(e) => set("namaLengkap", e.target.value)} />
                  </div>
                  <div>
                    <FieldLabel>Nomor WhatsApp</FieldLabel>
                    <Input className="mt-2" data-testid="input-client-whatsapp" placeholder="081234567890" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
                  </div>
                  <div>
                    <FieldLabel>Email</FieldLabel>
                    <Input className="mt-2" type="email" data-testid="input-client-email" placeholder="nama@email.com" value={form.email} onChange={(e) => set("email", e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel>Alamat</FieldLabel>
                    <Textarea className="mt-2" data-testid="input-client-address" placeholder="Alamat lengkap Anda" value={form.alamat} onChange={(e) => set("alamat", e.target.value)} />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Jenis Acara</FieldLabel>
                    <Select value={form.jenisAcara} onValueChange={(v) => set("jenisAcara", v)}>
                      <SelectTrigger className="mt-2" data-testid="select-event-type"><SelectValue placeholder="Pilih jenis acara" /></SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FieldLabel>Tanggal Acara</FieldLabel>
                    <Input className="mt-2" type="date" data-testid="input-event-date" value={form.tanggalAcara} onChange={(e) => set("tanggalAcara", e.target.value)} />
                  </div>
                  <div>
                    <FieldLabel>Waktu Acara</FieldLabel>
                    <Input className="mt-2" type="time" data-testid="input-event-time" value={form.waktuAcara} onChange={(e) => set("waktuAcara", e.target.value)} />
                  </div>
                  <div>
                    <FieldLabel>Jumlah Tamu</FieldLabel>
                    <Select value={form.jumlahTamu} onValueChange={(v) => set("jumlahTamu", v)}>
                      <SelectTrigger className="mt-2" data-testid="select-event-guests"><SelectValue placeholder="Estimasi tamu" /></SelectTrigger>
                      <SelectContent>
                        {GUEST_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="grid gap-5">
                  <div>
                    <FieldLabel>Nama Tempat</FieldLabel>
                    <Input className="mt-2" data-testid="input-venue-name" placeholder="Contoh: Glass House Hotel" value={form.namaTempat} onChange={(e) => set("namaTempat", e.target.value)} />
                  </div>
                  <div>
                    <FieldLabel>Alamat Lengkap</FieldLabel>
                    <Textarea className="mt-2" data-testid="input-venue-address" placeholder="Jl. ..." value={form.alamatLengkap} onChange={(e) => set("alamatLengkap", e.target.value)} />
                  </div>
                  <div>
                    <FieldLabel>Link Google Maps (opsional)</FieldLabel>
                    <Input className="mt-2" data-testid="input-venue-maps" placeholder="https://maps.google.com/..." value={form.linkMaps} onChange={(e) => set("linkMaps", e.target.value)} />
                  </div>
                  <FileUpload label="Upload Foto Lokasi (opsional)" description="Foto area gedung/halaman untuk estimasi tim" testid="upload-venue-photo" previewUrl={form.fotoLokasiUrl} uploading={uploading.fotoLokasi} onUpload={(f) => uploadFile("fotoLokasi", f)} />
                </div>
              )}

              {step === 3 && (
                <div className="grid gap-5">
                  <div>
                    <FieldLabel>Paket Dekorasi</FieldLabel>
                    <Select value={form.paket} onValueChange={(v) => set("paket", v)}>
                      <SelectTrigger className="mt-2" data-testid="select-chosen-package"><SelectValue placeholder="Pilih paket" /></SelectTrigger>
                      <SelectContent>
                        {pkgList.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} — {formatRupiah(p.price)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <FieldLabel>Tema (opsional)</FieldLabel>
                      <Input className="mt-2" data-testid="input-theme" placeholder="Contoh: Rustic Garden" value={form.tema} onChange={(e) => set("tema", e.target.value)} />
                    </div>
                    <div>
                      <FieldLabel>Warna Dominan (opsional)</FieldLabel>
                      <Input className="mt-2" data-testid="input-color" placeholder="Contoh: Gold & Ivory" value={form.warnaDominan} onChange={(e) => set("warnaDominan", e.target.value)} />
                    </div>
                  </div>
                  <FileUpload label="Upload Foto Referensi (opsional)" description="Foto dari Pinterest/Instagram sebagai rujukan" testid="upload-reference-photo" previewUrl={form.fotoReferensiUrl} uploading={uploading.fotoReferensi} onUpload={(f) => uploadFile("fotoReferensi", f)} />
                  <div>
                    <FieldLabel>Catatan Tambahan (opsional)</FieldLabel>
                    <Textarea className="mt-2" data-testid="input-decor-notes" placeholder="Request bunga favorit, backdrop khusus..." value={form.catatanTambahan} onChange={(e) => set("catatanTambahan", e.target.value)} />
                  </div>
                </div>
              )}

              <div className="mt-10 flex items-center justify-between">
                <button onClick={back} disabled={step === 0} data-testid="booking-back-button" className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-semibold tracking-widest uppercase transition-all ${step === 0 ? "opacity-0 pointer-events-none" : "border border-[#E5D7C5] text-[#4A443F] hover:border-[#C5A059]"}`}>
                  <ArrowLeft className="w-4 h-4" /> Kembali
                </button>
                <button onClick={next} data-testid="booking-next-button" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#1C1917] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#C5A059] transition-all">
                  {step < 3 ? "Lanjut" : "Lihat Ringkasan"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* ---------------- SUMMARY ---------------- */}
          {mode === "summary" && (
            <motion.div key="summary" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-3xl border border-[#E5D7C5] shadow-[0_4px_20px_rgba(28,25,23,0.05)] p-6 sm:p-10" data-testid="summary-view">
              <h3 className="font-serif text-2xl text-[#1C1917] mb-6">Ringkasan Booking</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <SummaryBlock title="Data Client" rows={[["Nama", form.namaLengkap], ["WhatsApp", form.whatsapp], ["Email", form.email], ["Alamat", form.alamat]]} />
                <SummaryBlock title="Detail Acara" rows={[["Jenis", form.jenisAcara], ["Tanggal", form.tanggalAcara], ["Waktu", form.waktuAcara], ["Jumlah Tamu", form.jumlahTamu]]} />
                <SummaryBlock title="Lokasi" rows={[["Tempat", form.namaTempat], ["Alamat", form.alamatLengkap], ["Maps", form.linkMaps || "-"]]} />
                <SummaryBlock title="Paket & Konsep" rows={[["Paket", form.paket], ["Tema", form.tema || "-"], ["Warna", form.warnaDominan || "-"], ["Catatan", form.catatanTambahan || "-"]]} />
              </div>

              <div className="mt-8 rounded-2xl bg-[#F7F0E6] border border-[#E5D7C5] p-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#4A443F]">Total Harga</span>
                  <span className="font-serif text-2xl font-semibold text-[#1C1917]" data-testid="summary-total">{formatRupiah(total)}</span>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E5D7C5]">
                  <span className="text-sm text-[#4A443F]">DP Minimal <span className="font-semibold text-[#C5A059]">50%</span></span>
                  <span className="font-serif text-2xl font-semibold text-[#C5A059]" data-testid="summary-dp">{formatRupiah(dp)}</span>
                </div>
              </div>

              <label className="mt-6 flex items-start gap-3 cursor-pointer">
                <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="checkbox-agree-terms" className="mt-0.5" />
                <span className="text-sm text-[#4A443F]">Saya telah memeriksa dan menyetujui data booking di atas serta syarat & ketentuan pemesanan dekorasi Ascarya.</span>
              </label>

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <button onClick={() => setMode("form")} data-testid="summary-back-button" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#E5D7C5] text-[#4A443F] text-xs font-semibold tracking-widest uppercase hover:border-[#C5A059] transition-all">
                  <ArrowLeft className="w-4 h-4" /> Edit Data
                </button>
                <button onClick={submitBooking} disabled={submitting} data-testid="confirm-booking-summary-button" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] transition-all disabled:opacity-60">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Konfirmasi Booking <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </motion.div>
          )}

          {/* ---------------- PAYMENT ---------------- */}
          {mode === "payment" && booking && (
            <motion.div key="payment" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-3xl border border-[#E5D7C5] shadow-[0_4px_20px_rgba(28,25,23,0.05)] p-6 sm:p-10" data-testid="payment-view">
              <div className="text-center mb-8">
                <ShieldCheck className="w-10 h-10 text-[#C5A059] mx-auto mb-3" />
                <h3 className="font-serif text-2xl text-[#1C1917]">Amankan Tanggal Anda</h3>
                <p className="text-[#786F66] mt-2 text-sm">Selesaikan pembayaran DP minimal 50% untuk mengunci jadwal dekorasi.</p>
                <div className="mt-4 inline-block rounded-xl bg-[#F7F0E6] border border-[#E5D7C5] px-6 py-3">
                  <div className="text-[10px] tracking-widest uppercase text-[#967C52]">DP Minimal 50%</div>
                  <div className="font-serif text-3xl font-semibold text-[#C5A059]">{formatRupiah(booking.dp_amount)}</div>
                </div>
              </div>

              <div className="flex gap-2 mb-6">
                {["Bank Transfer", "QRIS"].map((m) => (
                  <button key={m} onClick={() => setPayMethod(m)} data-testid={`pay-method-${m.replace(/\s/g, "").toLowerCase()}`} className={`flex-1 py-3 rounded-xl text-xs font-semibold tracking-widest uppercase border transition-all ${payMethod === m ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white text-[#4A443F] border-[#E5D7C5] hover:border-[#C5A059]"}`}>{m}</button>
                ))}
              </div>

              {payMethod === "Bank Transfer" ? (
                <div className="space-y-3">
                  {banks.map((b) => (
                    <div key={b.bank} className="flex items-center justify-between rounded-xl border border-[#E5D7C5] bg-[#FFFDF9] p-4">
                      <div>
                        <div className="font-semibold text-[#1C1917]">{b.bank}</div>
                        <div className="font-mono text-lg text-[#C5A059]">{b.account}</div>
                        <div className="text-xs text-[#786F66]">a.n. {b.holder}</div>
                      </div>
                      <button onClick={() => copyText(b.account)} data-testid={`copy-${b.bank.split(" ")[1].toLowerCase()}-account`} className="p-2 rounded-lg border border-[#E5D7C5] hover:border-[#C5A059] text-[#C5A059]"><Copy className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center rounded-xl border border-[#E5D7C5] bg-[#FFFDF9] p-6">
                  <img src={qris} alt="QRIS Ascarya Dekorasi" className="w-56 h-56" data-testid="qris-image" />
                  <p className="text-xs text-[#786F66] mt-3 text-center">Scan QRIS menggunakan aplikasi e-wallet atau mobile banking Anda (GoPay, OVO, Dana, ShopeePay).</p>
                </div>
              )}

              <div className="mt-6">
                <FileUpload label="Upload Bukti Pembayaran" description="Screenshot / foto bukti transfer DP" testid="upload-payment-proof-input" previewUrl={proof.url} uploading={uploading.proof} onUpload={async (file) => {
                  setUploading((u) => ({ ...u, proof: true }));
                  try {
                    const fd = new FormData();
                    fd.append("file", file);
                    const { data } = await axios.post(`${API}/upload`, fd);
                    setProof({ path: data.path, url: `${BACKEND_URL}${data.url}` });
                    toast.success("Bukti pembayaran diunggah");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Gagal mengunggah");
                  } finally {
                    setUploading((u) => ({ ...u, proof: false }));
                  }
                }} />
              </div>

              <button onClick={submitPayment} disabled={submitting} data-testid="submit-payment-proof-button" className="mt-8 w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] transition-all disabled:opacity-60">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Kirim Bukti Pembayaran <ArrowRight className="w-4 h-4" /></>}
              </button>
            </motion.div>
          )}

          {/* ---------------- SUCCESS ---------------- */}
          {mode === "success" && booking && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl border border-[#E5D7C5] shadow-[0_4px_20px_rgba(28,25,23,0.05)] p-8 sm:p-12 text-center" data-testid="success-view">
              <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-[#2E7D32]" />
              </div>
              <h3 className="font-serif text-3xl text-[#1C1917]">Booking Berhasil</h3>
              <p className="mt-3 text-[#4A443F] max-w-md mx-auto">
                Terima kasih, <span className="font-semibold">{booking.client.namaLengkap}</span>. Data booking Anda telah berhasil dikirim.
              </p>

              <div className="mt-8 rounded-2xl bg-[#F7F0E6] border border-[#E5D7C5] p-6 text-left max-w-md mx-auto">
                <div className="text-[10px] tracking-widest uppercase text-[#967C52]">Booking ID</div>
                <div className="font-mono text-2xl font-semibold text-[#1C1917]" data-testid="success-booking-id">{booking.booking_code}</div>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#786F66]">Tanggal</span><span className="text-[#1C1917] font-medium">{booking.event.tanggalAcara}</span></div>
                  <div className="flex justify-between"><span className="text-[#786F66]">Paket</span><span className="text-[#1C1917] font-medium">{booking.concept.paket}</span></div>
                  <div className="flex justify-between"><span className="text-[#786F66]">Lokasi</span><span className="text-[#1C1917] font-medium">{booking.location.namaTempat}</span></div>
                  <div className="flex justify-between items-center pt-2 border-t border-[#E5D7C5]"><span className="text-[#786F66]">Status</span><span className="inline-flex items-center gap-1.5 text-[#D97706] font-semibold text-xs bg-[#D97706]/10 px-3 py-1 rounded-full">🟡 {booking.status}</span></div>
                </div>
              </div>

              <p className="mt-6 text-sm text-[#786F66] max-w-md mx-auto italic">
                Tim Ascarya Dekorasi akan memeriksa data booking dan menghubungi Anda melalui WhatsApp.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => setMode("detail_toast")} data-testid="btn-view-booking-detail" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-[#E5D7C5] text-[#4A443F] text-xs font-semibold tracking-widest uppercase hover:border-[#C5A059] transition-all">
                  <FileText className="w-4 h-4" /> Lihat Booking
                </button>
                <a href={`https://wa.me/${waNum}?text=${encodeURIComponent(`Halo Admin Ascarya Dekorasi, saya ingin mengonfirmasi booking dengan ID: ${booking.booking_code}.`)}`} target="_blank" rel="noreferrer" data-testid="btn-chat-whatsapp-admin" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C5A059] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[#A8833E] transition-all">
                  <MessageCircle className="w-4 h-4" /> Chat WhatsApp Admin
                </a>
              </div>

              <button onClick={resetAll} data-testid="new-booking-button" className="mt-6 text-xs text-[#786F66] hover:text-[#C5A059] underline">
                Buat booking baru
              </button>
            </motion.div>
          )}

          {mode === "detail_toast" && booking && (
            <motion.div key="detail" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-[#E5D7C5] shadow-[0_4px_20px_rgba(28,25,23,0.05)] p-6 sm:p-10" data-testid="detail-view">
              <h3 className="font-serif text-2xl text-[#1C1917] mb-6">Detail Booking — {booking.booking_code}</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <SummaryBlock title="Data Client" rows={[["Nama", booking.client.namaLengkap], ["WhatsApp", booking.client.whatsapp], ["Email", booking.client.email], ["Alamat", booking.client.alamat]]} />
                <SummaryBlock title="Detail Acara" rows={[["Jenis", booking.event.jenisAcara], ["Tanggal", booking.event.tanggalAcara], ["Waktu", booking.event.waktuAcara], ["Tamu", booking.event.jumlahTamu]]} />
                <SummaryBlock title="Lokasi" rows={[["Tempat", booking.location.namaTempat], ["Alamat", booking.location.alamatLengkap]]} />
                <SummaryBlock title="Pembayaran" rows={[["Total", formatRupiah(booking.total_harga)], ["DP (50%)", formatRupiah(booking.dp_amount)], ["Metode", booking.payment_method || "-"], ["Status", booking.status]]} />
              </div>
              <button onClick={() => setMode("success")} className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#E5D7C5] text-[#4A443F] text-xs font-semibold tracking-widest uppercase hover:border-[#C5A059] transition-all">
                <ArrowLeft className="w-4 h-4" /> Kembali
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const SummaryBlock = ({ title, rows }) => (
  <div className="rounded-2xl border border-[#E5D7C5] bg-[#FFFDF9] p-5">
    <div className="font-mono text-[10px] tracking-widest uppercase text-[#C5A059] mb-3">{title}</div>
    <dl className="space-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 text-sm">
          <dt className="text-[#786F66] shrink-0">{k}</dt>
          <dd className="text-[#1C1917] font-medium text-right break-words">{v || "-"}</dd>
        </div>
      ))}
    </dl>
  </div>
);
